/**
 * NEXORA AI - Progress Bar Component (Refactored)
 * Epic 1.3 Phase 2.2 - Onboarding Premium
 * 
 * Specifications:
 * - Always visible (directive PO)
 * - Responsive mobile-first
 * - Shows progress visually + numerically
 * - Touch-friendly back button (44x44px)
 * - Accessible (ARIA labels)
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft } from 'lucide-react';
import { motion } from 'framer-motion';

export default function ProgressBar({ currentStep, totalSteps, onBack, canGoBack = true }) {
  const { t } = useTranslation('onboarding');
  const percentage = ((currentStep + 1) / totalSteps) * 100;

  return (
    <div className="fixed top-0 left-0 right-0 z-40 bg-zinc-900/95 backdrop-blur-xl border-b border-zinc-800/50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
        <div className="flex items-center gap-3 sm:gap-4">
          {/* Back button - Touch optimized 44x44px */}
          {canGoBack && currentStep > 0 && (
            <button
              onClick={onBack}
              className="flex-shrink-0 w-11 h-11 flex items-center justify-center rounded-lg bg-zinc-800/50 hover:bg-zinc-700 border border-zinc-700/50 text-zinc-300 hover:text-white transition-all active:scale-95"
              aria-label={t('back')}
              data-testid="progress-back-button"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
          )}

          {/* Progress info + bar */}
          <div className="flex-1 min-w-0">
            {/* Step indicator + Percentage */}
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs sm:text-sm font-semibold text-white">
                {t('step_of', { current: currentStep + 1, total: totalSteps })}
              </span>
              <motion.span
                key={percentage}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-xs sm:text-sm font-bold text-blue-400"
              >
                {Math.round(percentage)}%
              </motion.span>
            </div>

            {/* Progress bar */}
            <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
              <motion.div
                className="h-full bg-gradient-to-r from-blue-600 to-purple-600 rounded-full"
                initial={{ width: 0 }}
                animate={{ width: `${percentage}%` }}
                transition={{ duration: 0.5, ease: 'easeOut' }}
                data-testid="progress-bar-fill"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
